var SampleProduct = class extends HTMLElement {
  _customProperty = '__added_from_theme';
  _cart;
  _settings;

  constructor() {
    super();

    this._cart = null;
    this._settings = null;

    document.addEventListener("cart:change", this.updateCartState.bind(this));
  }

  /**
   * -------------------------------------------------------------------------------------------------------------------
   * PUBLIC API (PROPERTIES)
   * -------------------------------------------------------------------------------------------------------------------
   */

  /**
   * Get the Variant ID of the sample product
   * @returns {string}
   */
  get sampleVariantId() {
    return this.dataset['variantId'];
  }

  /**
   * Gets some data regarding the sample product in the cart.
   *
   * @returns {{hasDiscount: boolean, addedByTheme: boolean, index: number, present: boolean, key: string}}
   */
  get sampleProductInCart() {
    const data = {
      present: false,
      addedByTheme: false,
      index: -1,
      key: '',
      hasDiscount: false
    };
    let index = -1;
    for (const item of this.cart.items) {
      index++;
      if (`${item.variant_id}` === this.sampleVariantId) {
        data.present = true;
        data.addedByTheme = typeof item.properties[this._customProperty] !== "undefined";
        data.index = index;
        data.key = item.key;
        data.hasDiscount = item.discounts.length > 0;
        // there might be a case when there's a sample added by the system, and added by the user.
        // we want to select the one added by the system if there is one, otherwise fall back to the user-added
        if (data.addedByTheme)
          break;
      }
    }

    return data;
  }

  /**
   * Get data
   * @returns {{ sample_logic: string, sample_minimum_cart_total: number, sample_separate_products_in_cart: number, sample_given_products_in_cart: number[], sample_one_product_satisfies: boolean }}
   */
  get data() {
    if (this._settings === null) {
      this._settings = JSON.parse(this.dataset['settings']);
    }
    return this._settings;
  }

  /**
   * Gets the sample logic
   * @returns {"minimum_cart_total"|"separate_products_in_cart"|"given_products_in_cart"}
   */
  get sampleLogic() {
    return this.data.sample_logic;
  }

  /**
   * Gets the minimum cart total for receiving a sample
   * @returns {number}
   */
  get minimumTotal() {
    return Number(this.data.sample_minimum_cart_total);
  }

  /**
   * Gets the amount of separate products to be present in the cart for receiving a sample
   * @returns {number}
   */
  get minimumItems() {
    return Number(this.data.sample_separate_products_in_cart);
  }

  /**
   * Gets the IDs of products which need to be in cart for receiving a sample
   * @returns {number[]}
   */
  get givenProducts() {
    return this.data.sample_given_products_in_cart;
  }

  /**
   * Gets whether one product is enough to be present in the cart from the given products for receiving a free sample
   * @returns {boolean}
   */
  get oneProductSatisfies() {
    return this.data.sample_one_product_satisfies;
  }

  /**
   * Get the current cart state
   * @returns {Object}
   */
  get cart() {
    return this._cart;
  }

  /**
   * Set the current cart state
   * @param cart the event when cart:change is raised
   */
  set cart(cart) {
    this._cart = cart;
  }

  /**
   * -------------------------------------------------------------------------------------------------------------------
   * EVENT LISTENERS
   * -------------------------------------------------------------------------------------------------------------------
   */
  /**
   * Update cart state after change
   * @param event the event parameters
   */
  updateCartState(event) {
    this.cart = event.detail?.cart ?? this.cart;
    this.cartChange.call(this);
  }

  /**
   * Handle cart change
   */
  cartChange() {
    switch (this.sampleLogic) {
      case "given_products_in_cart":
        this.givenProductsAddedToCart().then(this.postCartChange.bind(this));
        break;
      case "minimum_cart_total":
        this.minimumCartTotal().then(this.postCartChange.bind(this));
        break;
      case "separate_products_in_cart":
        this.separateProductsInCart().then(this.postCartChange.bind(this));
        break;
    }
  }
  postCartChange(added) {
    if (!added) {
      const state = this.sampleProductInCart;
      if (!state.hasDiscount && state.addedByTheme) {
        this.removeSample(state.index, state.key);
      }
    }
  }

  /**
   * Used when the cart needs to hit a minimum total price for a sample product
   * @returns {Promise<boolean>}
   */
  async minimumCartTotal() {
    const cartTotal = this.cart.total_price;
    const cartMinimum = Math.floor(this.minimumTotal * 100);
    const sampleProductExists = this.sampleProductInCart.present;

    if (!sampleProductExists && cartTotal >= cartMinimum) {
      await this.addSample();
      return true;
    }
    return false;
  }

  /**
   * Used when a specific amount of products need to be present in the cart
   * @returns {Promise<boolean>}
   */
  async separateProductsInCart() {
    const satisfactoryAmount = this.minimumItems;
    let separateProducts = this.cart.items.length;
    const sampleProductExists = this.sampleProductInCart.present;

    if (sampleProductExists) {
      separateProducts -= 1;
    }

    if (!sampleProductExists && separateProducts >= satisfactoryAmount) {
      await this.addSample();
      return true;
    }
    return false;
  }

  /**
   * Used when given products need to be present in the cart
   * @returns {Promise<boolean>}
   */
  async givenProductsAddedToCart() {
    const givenProducts = this.givenProducts;
    const oneProductSatisfies = this.oneProductSatisfies;
    const sampleProductExists = this.sampleProductInCart.present;
    const givenProductsInCart = this.cart.items.filter((item) => givenProducts.includes(item.product_id));

    // ez a check mire kell
    for (const product of givenProductsInCart) {
      if (`${product.variant_id}` === this.sampleVariantId) {
        givenProductsInCart.splice(givenProductsInCart.indexOf(product), 1);
      }
    }

    if (!sampleProductExists &&
        ((oneProductSatisfies && givenProductsInCart.length >= 1)
        || givenProductsInCart.length === givenProducts.length)) {
      await this.addSample();
      return true;
    }
    return false;
  }

  /**
   * Adds the sample product
   *
   * @returns {Promise<void>}
   */
  async addSample() {
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));
    const formData = new FormData();
    formData.set("form_type", "product");
    formData.set("id", `${this.sampleVariantId}`);
    formData.set("quantity", "1");
    formData.set("sections", sectionsToBundle.join(","));
    formData.set("sections_url", `${Shopify.routes.root}variants/${this.id.value}`);
    formData.set(`properties[${this._customProperty}]`, '_1');

    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      body: formData,
      method: "POST",
      headers: {
        "X-Requested-With": "XMLHttpRequest"
      }
    });

    const responseJson = await response.json();
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    if (response.ok) {
      if (window.themeVariables.settings.cartType === "page" || window.themeVariables.settings.pageType === "cart") {
        window.location.href = `${Shopify.routes.root}cart`;
        return;
      }

      const cartContent = JSON.parse(JSON.stringify(this.cart));
      cartContent["sections"] = responseJson["sections"];
      cartContent.items.push(responseJson);

      this.dispatchEvent(new CustomEvent("variant:add", {
        bubbles: true,
        detail: {
          items: [responseJson],
          cart: cartContent
        }
      }));

      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: "variant:add",
          cart: cartContent
        }
      }));
    } else {
      this.dispatchEvent(new CustomEvent("cart:error", {
        bubbles: true,
        detail: {
          error: responseJson["description"]
        }
      }));
    }
  }

  /**
   * Removes the sample product
   *
   * @returns {Promise<void>}
   */
  async removeSample(index, lineKey) {
    if (window.themeVariables.settings.pageType === "cart") {
      window.location.href = `${Shopify.routes.root}cart/change?id=${lineKey}&quantity=0`;
    } else {
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
      const lineItem = document.querySelector(`.cart-drawer__items:nth-child(${index})`)
      lineItem?.dispatchEvent(new CustomEvent("line-item:will-change", { bubbles: true, detail: { targetQuantity: 0 } }));
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));
      const cartContent = await (await fetch(`${Shopify.routes.root}cart/change.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
          sections: sectionsToBundle.join(",")
        })
      })).json();
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      const lineItemAfterChange = cartContent["items"].filter((lineItem2) => lineItem2["key"] === lineKey);
      lineItem?.dispatchEvent(new CustomEvent("line-item:change", {
        bubbles: true,
        detail: {
          quantity: lineItemAfterChange.length === 0 ? 0 : lineItemAfterChange[0]["quantity"],
          cart: cartContent
        }
      }));
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: "line-item:change",
          cart: cartContent
        }
      }));
    }
  }
}
window.customElements.define('sample-product', SampleProduct);

var SampleSelector = class extends HTMLElement {
  constructor() {
    super();

    const complementaryOptions = this.querySelectorAll('.complementary--variant-option');
    const complementaryInput = this.querySelector('[name="id"]');

    if (complementaryInput) {
      complementaryInput.setAttribute('value', this.dataset.productId);
      if (complementaryOptions.length > 1) {
        for (const complementaryOption of complementaryOptions) {
          complementaryOption.addEventListener('click', () => {
            complementaryInput.setAttribute('value', complementaryOption.dataset.id);
          });
        }
      }
    }
  }
}
window.customElements.define('sample-selector', SampleSelector);

var QuickBuyVariantsCustom = class extends HTMLElement {
  constructor() {
    super();

    this.cart = null;
    this.buttons = this.querySelectorAll('button[data-varaint-id]');
    this.init();
  }
  
  init() {
    this.buttons.forEach(btn => {
      const variantId = btn.dataset.varaintId;
      btn.addEventListener('click', () => this.addToCart(variantId));
    })
  }

  async addToCart(variantId) {
    console.log(`variantId: ${variantId}`)
    this.disableButtons();
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));    
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));

    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1
          }],
        sections: sectionsToBundle.join(",")
      }),
      method: "POST",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        'Content-Type': 'application/json'
      }
    });

    const responseJson = await response.json();
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    if (response.ok) {
      if (window.themeVariables.settings.cartType === "page" || window.themeVariables.settings.pageType === "cart") {
        window.location.href = `${Shopify.routes.root}cart`;
        return;
      }

      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      
      console.log('cartContent => ', cartContent)
      console.log('responseJson => ', responseJson)
      cartContent["sections"] = responseJson["sections"];
      cartContent.items.concat(responseJson?.items);

      this.dispatchEvent(new CustomEvent("variant:add", {
        bubbles: true,
        detail: {
          items: [responseJson],
          cart: cartContent
        }
      }));

      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: "variant:add",
          cart: cartContent
        }
      }));
      this.enableButtons();
    } else {
      this.enableButtons();
      this.dispatchEvent(new CustomEvent("cart:error", {
        bubbles: true,
        detail: {
          error: responseJson["description"]
        }
      }));
    }
  }

  disableButtons() {
    this.buttons.forEach(btn => {
      if (!btn.hasAttribute('disabled')) {
        btn.setAttribute('disabled', true);
      }
    })
  }

  enableButtons() {
    this.buttons.forEach(btn => {
      const isProductAvaliable = btn.dataset.varaintAvailable;

      if (isProductAvaliable == 'true' && btn.hasAttribute('disabled')) {
        btn.removeAttribute('disabled');
      }
    })
  }
}
window.customElements.define('quick-buy-variants-custom', QuickBuyVariantsCustom);

/**
* ----------------------------------------------------------------------------
* Super hover on mobile
* ----------------------------------------------------------------------------
*/

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll('.product-card__figure').forEach(el => {
    const imageElement = el.querySelector('a[data-instant]');

    imageElement.addEventListener("touchstart", function() {
      setTimeout(() => {
        el.classList.add('product-card__figure_super-hover');
      }, 200);
      
    });
    imageElement.addEventListener("touchend", function() {
      setTimeout(() => {
        el.classList.remove('product-card__figure_super-hover');
      }, 100);
    });
  })
});

/**
* ----------------------------------------------------------------------------
* Product card mobile quidck buy button
* ----------------------------------------------------------------------------
*/

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll('button[product-quick-buy-id]').forEach(el => {
    el.addEventListener("click", function(e) {
      const quickBuyID = e.target.getAttribute('product-quick-buy-id');

      if (quickBuyID) {
        const productQuickBuyContainer = document.querySelector(`div[product-quick-buy-container="${quickBuyID}"]`);

        if (productQuickBuyContainer) {
          productQuickBuyContainer.classList.toggle('product-card__quick-buy-custom-open');
        }

        const plusButtonContainer = document.querySelector(`button[product-quick-buy-id="${quickBuyID}"]`).parentElement;
        
        if (productQuickBuyContainer.classList.contains('product-card__quick-buy-custom-open')) {
          plusButtonContainer.style.opacity = "0";
          plusButtonContainer.style.visibility = "hidden";
        } else {
          plusButtonContainer.style.opacity = "1";
          plusButtonContainer.style.visibility = "visible";
        }
      }
    });
  })
});

/**
* ----------------------------------------------------------------------------
* Slider RAO
* ----------------------------------------------------------------------------
*/

function togglePlayPause(el) {
  if (el.paused) {
      el.play();
  } else {
      el.pause();
  }
}

function togglePlay(el) {
  el.play();
}

function togglePause(el) {
  el.pause();
}

document.addEventListener("DOMContentLoaded", () => {
  const playPauseBtns = document.querySelectorAll('.play-button');
  playPauseBtns.forEach(ppb => {
    const video = ppb.parentElement.querySelector('video');
    const playIcon = ppb.querySelector('.playIcon');
    const pauseIcon = ppb.querySelector('.pauseIcon');
  
    video.addEventListener('play', () => {
        ppb.parentElement.classList.remove('paused');
        ppb.parentElement.classList.add('playing');
        playIcon.style.display = 'none';
        pauseIcon.style.display = 'flex';
    });
    video.addEventListener('pause', () => {
        ppb.parentElement.classList.remove('playing');
        ppb.parentElement.classList.add('paused');
        playIcon.style.display = 'flex';
        pauseIcon.style.display = 'none';
    });
    ppb.addEventListener('click', () => {
      if (window.innerWidth <= 999) {
        togglePlayPause(video);
      }
    });
  
    ppb.parentElement.addEventListener('mouseover', () => {
      if (window.innerWidth > 1000) {
        togglePlay(video);
      }
    });
  
    ppb.parentElement.addEventListener('mouseout', () => {
      if (window.innerWidth > 1000) {
        togglePause(video);
      }
    });
  })
});

var CollectionImageSwitcher = class extends HTMLElement {
  constructor() {
    super();

    this.primaryImageButton = this.querySelector('#primary-image-button');
    this.secondaryImageButton = this.querySelector('#secondary-image-button');
    this.primaryImageButton = this.querySelector('#primary-image-button');
    this.secondaryImageOption1 = this.querySelector('#secondary-image-option-1');
    this.secondaryImageOption2 = this.querySelector('#secondary-image-option-2');
    this.secondaryOptionsElement = this.querySelector('.image-switcher-bottom-bar');
    this.collectionElement = this.closest('.collection');

    if (this.primaryImageButton) {
      this.primaryImageButton.addEventListener('click', () => {
        this.imageButtonClick('primary');
      })
    }

    if (this.secondaryImageButton) {
      this.secondaryImageButton.addEventListener('click', () => {
        this.imageButtonClick('secondary');
      })
    }

    if (this.secondaryImageOption1) {
      this.secondaryImageOption1.addEventListener('click', () => {
        this.imageButtonClick('option1');
      })
    }

    if (this.secondaryImageOption2) {
      this.secondaryImageOption2.addEventListener('click', () => {
        this.imageButtonClick('option2');
      })
    }
  }

  imageButtonClick(type) {
    if (type == 'option1') {
      if (this.secondaryImageOption2.classList.contains('selected')) {
        this.secondaryImageOption2.classList.remove('selected');
      }
      if (!this.secondaryImageOption1.classList.contains('selected')) {
        this.secondaryImageOption1.classList.add('selected');
        this.collectionElement.classList.add('show-secondary-image-1');
        this.collectionElement.classList.remove('show-secondary-image-2');
      }
    }

    if (type == 'option2') {
      if (this.secondaryImageOption1.classList.contains('selected')) {
        this.secondaryImageOption1.classList.remove('selected');
      }
      if (!this.secondaryImageOption2.classList.contains('selected')) {
        this.secondaryImageOption2.classList.add('selected');
        this.collectionElement.classList.add('show-secondary-image-2');
        this.collectionElement.classList.remove('show-secondary-image-1');
      }
    }

    if (type == 'primary') {
      if (this.secondaryImageButton.classList.contains('selected')) {
        this.secondaryImageButton.classList.remove('selected');
        this.secondaryOptionsElement.classList.remove('image-switcher-bottom-bar--visible');
      }
      if (!this.primaryImageButton.classList.contains('selected')) {
        this.primaryImageButton.classList.add('selected');
      }

      if (this.collectionElement && (this.collectionElement.classList.contains('show-secondary-image-1') || this.collectionElement.classList.contains('show-secondary-image-2'))) {
        if (this.collectionElement.classList.contains('show-secondary-image')) {
          this.collectionElement.classList.remove('show-secondary-image');
        }

        if (this.collectionElement.classList.contains('show-secondary-image-1')) {
          this.collectionElement.classList.remove('show-secondary-image-1');
        }
        if (this.collectionElement.classList.contains('show-secondary-image-2')) {
          this.collectionElement.classList.remove('show-secondary-image-2');
        }       
      }
    }
    
    if (type == 'secondary') {
      if (this.primaryImageButton.classList.contains('selected')) {
        this.primaryImageButton.classList.remove('selected');
      }
      if (!this.secondaryImageButton.classList.contains('selected')) {
        this.secondaryImageButton.classList.add('selected');
        this.secondaryOptionsElement.classList.add('image-switcher-bottom-bar--visible');

      }

      if (this.collectionElement && (!this.collectionElement.classList.contains('show-secondary-image-1') || !this.collectionElement.classList.contains('show-secondary-image-2'))) {
        if (!this.collectionElement.classList.contains('show-secondary-image')) {
          this.collectionElement.classList.add('show-secondary-image');
        }

        if (this.secondaryImageOption1.classList.contains('selected')) {
          this.collectionElement.classList.add('show-secondary-image-1');
          this.collectionElement.classList.remove('show-secondary-image-2');
        }

        if (this.secondaryImageOption2.classList.contains('selected')) {
          this.collectionElement.classList.add('show-secondary-image-2');
          this.collectionElement.classList.remove('show-secondary-image-1');
        }
      }
    }

    console.log('collectionElement => ', this.collectionElement)
  }
}
window.customElements.define('collection-image-switcher', CollectionImageSwitcher);